import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import cookie from 'cookie';
import { EventBus } from 'utils/EventBus';
import { useTenantCode, useTenantConfig } from 'utils/hooks/useTenantConfig';
import { InfoBox, Button } from '@beeline.com/bp-design-system';
import { useWarningBannerIntl } from './translations/useWarningBannerIntl';

const warningEnvs = new RegExp(['IMPL', 'SBOX', 'DEV'].join('|'), 'i');

export const WarningBanner: FC = () => {
  const intl = useWarningBannerIntl();
  const [isWarningBannerDisabled, setIsWarningBannerDisabled] = useState(
    !!cookie.parse(document.cookie).disableWarningBanner || localStorage.getItem('disableWarningBanner') === 'true'
  );
  const [isVisible, setVisibility] = useState(true);
  const tenantCode = useTenantCode();
  const tenantConfig = useTenantConfig();

  useEffect(() => {
    EventBus.on<'warning-banner', boolean>('warning-banner', (e) => {
      setIsWarningBannerDisabled(!e.detail);
      if (!e.detail) {
        localStorage.setItem('disableWarningBanner', 'true');
      } else {
        localStorage.removeItem('disableWarningBanner');
      }
    });
  });

  const targetEnv = tenantConfig?.tenantConfig?.environment;
  const isEnvWithWarning = warningEnvs.test(targetEnv ?? '');
  // We should hide the banner in test envs, unless we are testing the banner itself
  const showWarning = isEnvWithWarning && isVisible && !isWarningBannerDisabled;

  return (
    <>
      {showWarning && (
        <StyledWarningBanner data-test="warning-banner">
          <InfoBox
            title={intl.warningMessage({
              targetEnv,
              tenantCode
            })}
            type="ALERT"
            actions={
              <Button dataTest="hide-banner" onClick={() => setVisibility(false)}>
                <StyledDismissButton>{intl.dismissBtn}</StyledDismissButton>
              </Button>
            }
          />
        </StyledWarningBanner>
      )}
    </>
  );
};

const StyledWarningBanner = styled.div`
  position: fixed;
  // shadow from material-ui for snackbars with alerts
  box-shadow:
    0 3px 5px -1px rgb(0 0 0 / 20%),
    0 6px 10px 0 rgb(0 0 0 / 14%),
    0 1px 18px 0 rgb(0 0 0 / 12%);
  bottom: ${({ theme }) => theme.spacingSize.l};
  z-index: 100;
  left: 50%;
  transform: translateX(-50%);
`;

const StyledDismissButton = styled.span`
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
`;
